import { applySnapshot, types } from "mobx-state-tree"
import { flow } from "modules/common/models/flow"
import { Notificator } from "modules/common/models/notificator"
import { Transport } from "modules/common/models/transport"
import { apiUrls } from "modules/common/services/communication/urls"
import { v4 } from "uuid"

/** Лимиты versionControl: вложений в сообщении и размер одного файла */
export const FEEDBACK_MAX_ATTACHMENTS = 10
export const FEEDBACK_MAX_FILE_SIZE = 25 * 1024 * 1024

const PAGE_SIZE = 30

export interface FeedbackBranch {
  id: string
  kind: "admins" | "employee"
  subjectExternalId: string | null
  subjectName: string | null
  subjectPosition: string | null
  unreadCount: number
  lastReadSeq: number
  lastSeq: number
  staffReadSeq: number
  lastMessageAt: string | null
  lastMessageDirection: "in" | "out" | null
  lastMessagePreview: string | null
  lastMessageAuthorName: string | null
}

export interface FeedbackAttachment {
  id: string
  filename: string
  contentType: string
  size: number
  kind: string
}

export interface FeedbackMessage {
  id: string
  seq: number
  /** in — от сотрудников клиента разработчику, out — ответ разработчика */
  direction: "in" | "out"
  audience: "author" | "clientAdmins"
  text: string
  createdAt: string
  sentAt: string | null
  author: {
    kind: "client" | "staff"
    id: string
    name: string
    position: string | null
    isAdmin: boolean
  }
  attachments: FeedbackAttachment[]
  readByClient: { at: string; name: string } | null
}

interface MessagesPage {
  messages: FeedbackMessage[]
  hasMore: boolean
  oldestSeq: number | null
  staffReadSeq: number
}

/** Сообщение, которое ещё не подтвердил versionControl. clientMessageId стабилен между повторами */
export interface FeedbackOutgoing {
  clientMessageId: string
  branchId: string | null
  text: string
  attachments: FeedbackAttachment[]
  sentAt: string
  failed: boolean
  error: string
}

const errorText = (er: any) => (er && er.message ? er.message + "" : "Сервис обратной связи недоступен")

export const FeedbackStore = types
  .compose(
    Transport,
    Notificator,
    types.model({
      /** статус загружен: до этого кнопку не показываем */
      statusLoaded: types.boolean,
      enabled: types.boolean,
      isAdmin: types.boolean,
      /** полное ФИО, под которым Rost пишет сотрудника в versionControl */
      viewerName: types.string,
      unread: types.number,

      branches: types.frozen<FeedbackBranch[]>([]),
      branchesLoaded: types.boolean,
      branchesLoading: types.boolean,
      branchesError: types.string,

      /** null — ветки ещё нет: первое сообщение заведёт её */
      activeBranchId: types.maybeNull(types.string),
      messages: types.frozen<FeedbackMessage[]>([]),
      hasMore: types.boolean,
      oldestSeq: types.maybeNull(types.number),
      staffReadSeq: types.number,
      messagesLoading: types.boolean,
      olderLoading: types.boolean,
      messagesError: types.string,

      outgoing: types.frozen<FeedbackOutgoing[]>([]),
    })
  )
  .views((self) => ({
    get activeBranch() {
      return self.branches.find((b) => b.id === self.activeBranchId) || null
    },

    get activeOutgoing() {
      return self.outgoing.filter((o) => o.branchId === self.activeBranchId)
    },
  }))
  .actions((self) => ({
    setMessages(page: MessagesPage, append: "replace" | "older" | "latest") {
      if (append === "older") {
        const known = new Set(self.messages.map((m) => m.id))
        self.messages = [...page.messages.filter((m) => !known.has(m.id)), ...self.messages]
        self.hasMore = page.hasMore
        self.oldestSeq = page.oldestSeq
      } else if (append === "latest" && self.messages.length > 0 && page.oldestSeq !== null) {
        // обновляем свежую страницу, не теряя уже подгруженную историю
        const older = self.messages.filter((m) => m.seq < (page.oldestSeq as number))
        self.messages = [...older, ...page.messages]
        if (older.length === 0) {
          self.hasMore = page.hasMore
          self.oldestSeq = page.oldestSeq
        }
      } else {
        self.messages = page.messages
        self.hasMore = page.hasMore
        self.oldestSeq = page.oldestSeq
      }

      self.staffReadSeq = page.staffReadSeq || 0
    },

    resetThread(branchId: string | null) {
      self.activeBranchId = branchId
      self.messages = []
      self.hasMore = false
      self.oldestSeq = null
      self.staffReadSeq = 0
      self.messagesError = ""
    },

    setOutgoing(list: FeedbackOutgoing[]) {
      self.outgoing = list
    },

    patchBranch(id: string, patch: Partial<FeedbackBranch>) {
      self.branches = self.branches.map((b) => (b.id === id ? { ...b, ...patch } : b))
    },
  }))
  .actions((self) => ({
    /** Сброс при входе: состояние прошлого пользователя не должно просочиться в новую сессию */
    reset() {
      applySnapshot(self, initialState())
    },

    loadStatus: flow(function* () {
      try {
        const status: { enabled: boolean; isAdmin: boolean; viewerName: string } = yield self.transport.get(
          apiUrls.feedback.status
        )
        self.enabled = status.enabled
        self.isAdmin = status.isAdmin
        self.viewerName = status.viewerName || ""
      } catch (er) {
        // кнопка просто не появится: мешать работе ошибкой в шапке незачем
        self.enabled = false
      } finally {
        self.statusLoaded = true
      }
    }),

    /** Бейдж на кнопке. Ошибки молча пропускаем: следующий опрос попробует снова */
    loadUnread: flow(function* () {
      if (!self.enabled) {
        return
      }

      try {
        const result: { unread: number } = yield self.transport.get(apiUrls.feedback.unread)
        self.unread = result.unread || 0
      } catch (er) {
        // ignore
      }
    }),

    loadMessages: flow(function* (mode: "replace" | "older" | "latest") {
      const branchId = self.activeBranchId

      if (!branchId) {
        return
      }

      const params: any = { branchId, limit: PAGE_SIZE }

      if (mode === "older") {
        if (!self.hasMore || self.oldestSeq === null || self.olderLoading) {
          return
        }

        params.before = self.oldestSeq
        self.olderLoading = true
      } else if (mode === "replace") {
        self.messagesLoading = true
      }

      try {
        const page: MessagesPage = yield self.transport.get(apiUrls.feedback.messages, { params })

        // пока грузили, пользователь мог переключиться на другую ветку
        if (self.activeBranchId === branchId) {
          self.setMessages(page, mode)
          self.messagesError = ""
        }
      } catch (er) {
        if (self.activeBranchId === branchId) {
          if (mode === "older") {
            self.notify.error(er)
          } else {
            self.messagesError = errorText(er)
          }
        }
      } finally {
        self.messagesLoading = false
        self.olderLoading = false
      }
    }),
  }))
  .actions((self) => ({
    /** Отметить ответы открытой ветки прочитанными */
    markRead: flow(function* () {
      const branch = self.activeBranch

      if (!branch || branch.unreadCount <= 0) {
        return
      }

      try {
        yield self.transport.post(apiUrls.feedback.read, { branchId: branch.id, upToSeq: branch.lastSeq })
        self.patchBranch(branch.id, { unreadCount: 0, lastReadSeq: branch.lastSeq })
        self.unread = self.branches.reduce((sum, b) => sum + (b.unreadCount || 0), 0)
      } catch (er) {
        // отметка не критична: попробуем при следующем открытии
      }
    }),
  }))
  .actions((self) => ({
    openBranch: flow(function* (branchId: string | null) {
      self.resetThread(branchId)

      if (branchId) {
        yield self.loadMessages("replace")
        yield self.markRead()
      }
    }),

    loadBranches: flow(function* (silent: boolean) {
      if (!silent) {
        self.branchesLoading = true
      }

      try {
        const branches: FeedbackBranch[] = yield self.transport.get(apiUrls.feedback.branches)
        self.branches = branches
        self.branchesLoaded = true
        self.branchesError = ""
        self.unread = branches.reduce((sum, b) => sum + (b.unreadCount || 0), 0)
        return true
      } catch (er) {
        if (!silent || !self.branchesLoaded) {
          self.branchesError = errorText(er)
        }
        return false
      } finally {
        self.branchesLoading = false
      }
    }),
  }))
  .actions((self) => ({
    /** Открытие диалога: ветки + выбор ветки по умолчанию */
    open: flow(function* () {
      const ok: any = yield self.loadBranches(false)

      if (!ok) {
        return
      }

      const current = self.activeBranchId && self.branches.find((b) => b.id === self.activeBranchId)

      if (current) {
        yield self.openBranch(current.id)
        return
      }

      // сотруднику приходит только его ветка; админу по умолчанию — чат администраторов
      const preferred = self.isAdmin
        ? self.branches.find((b) => b.kind === "admins") || self.branches[0]
        : self.branches[0]

      yield self.openBranch(preferred ? preferred.id : null)
    }),

    /** Периодическое обновление открытого диалога: ответы разработчика приходят без перезагрузки */
    refresh: flow(function* () {
      const ok: any = yield self.loadBranches(true)

      if (!ok) {
        return
      }

      if (self.activeBranchId) {
        yield self.loadMessages("latest")
        yield self.markRead()
      }
    }),

    uploadAttachment: flow(function* (file: File) {
      const form = new FormData()
      form.append("file", file)

      try {
        const result: FeedbackAttachment = yield self.transport.post(apiUrls.feedback.attachments, form)
        return result
      } catch (er) {
        self.notify.error(er)
        return null
      }
    }),
  }))
  .actions((self) => {
    const deliver = flow(function* (message: FeedbackOutgoing) {
      self.setOutgoing(
        self.outgoing.map((o) =>
          o.clientMessageId === message.clientMessageId ? { ...o, failed: false, error: "" } : o
        )
      )

      try {
        const result: { branchId: string } = yield self.transport.post(apiUrls.feedback.messages, {
          clientMessageId: message.clientMessageId,
          branchId: message.branchId,
          text: message.text,
          attachmentIds: message.attachments.map((a) => a.id),
          sentAt: message.sentAt,
        })

        self.setOutgoing(self.outgoing.filter((o) => o.clientMessageId !== message.clientMessageId))

        if (self.activeBranchId === message.branchId) {
          if (!self.activeBranchId) {
            // первое обращение завело ветку: переключаемся на неё
            yield self.loadBranches(true)
            yield self.openBranch(result.branchId)
          } else {
            yield self.loadMessages("latest")
            yield self.loadBranches(true)
          }
        }

        return true
      } catch (er) {
        self.setOutgoing(
          self.outgoing.map((o) =>
            o.clientMessageId === message.clientMessageId ? { ...o, failed: true, error: errorText(er) } : o
          )
        )

        return false
      }
    })

    return {
      send(text: string, attachments: FeedbackAttachment[]) {
        const message: FeedbackOutgoing = {
          clientMessageId: v4(),
          branchId: self.activeBranchId,
          text: text.trim(),
          attachments,
          sentAt: new Date().toISOString(),
          failed: false,
          error: "",
        }

        self.setOutgoing([...self.outgoing, message])
        return deliver(message)
      },

      /** Повтор с тем же clientMessageId: versionControl не создаст дубль */
      retry(clientMessageId: string) {
        const message = self.outgoing.find((o) => o.clientMessageId === clientMessageId)
        return message ? deliver(message) : Promise.resolve(false)
      },

      discard(clientMessageId: string) {
        self.setOutgoing(self.outgoing.filter((o) => o.clientMessageId !== clientMessageId))
      },
    }
  })
  .named("FeedbackStore")

export const initialState = (): FeedbackStoreSnapshotType => ({
  statusLoaded: false,
  enabled: false,
  isAdmin: false,
  viewerName: "",
  unread: 0,
  branches: [],
  branchesLoaded: false,
  branchesLoading: false,
  branchesError: "",
  activeBranchId: null,
  messages: [],
  hasMore: false,
  oldestSeq: null,
  staffReadSeq: 0,
  messagesLoading: false,
  olderLoading: false,
  messagesError: "",
  outgoing: [],
})

export type FeedbackStoreType = typeof FeedbackStore.Type
export type FeedbackStoreSnapshotType = typeof FeedbackStore.SnapshotType
